// NewsTicker.js — Enhanced UK live-ready version
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, Animated, Dimensions,
  TouchableOpacity, Linking, Alert, PanResponder,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

const CATEGORY_CONFIG = {
  jobs:      { color: '#0e4e96', icon: 'briefcase',           bg: '#e8f0fe' },
  salary:    { color: '#19abb5', icon: 'currency-gbp',        bg: '#e0f7fa' },
  news:      { color: '#fdb913', icon: 'newspaper-variant',   bg: '#fffbeb' },
  UK:        { color: '#fdb913', icon: 'flag',                bg: '#fffbeb' },
  trend:     { color: '#8b5cf6', icon: 'trending-up',         bg: '#f3e8ff' },
  education: { color: '#10b981', icon: 'school',              bg: '#d1fae5' },
  breaking:  { color: '#dc2626', icon: 'flash',               bg: '#fee2e2' },
  policy:    { color: '#f59e0b', icon: 'gavel',               bg: '#fef3c7' },
  default:   { color: '#fff',    icon: 'information',         bg: 'rgba(255,255,255,0.1)' },
};

const safeOpenUrl = async (url) => {
  if (!url) return false;
  try {
    const can = await Linking.canOpenURL(url);
    if (!can) throw new Error('Cannot open');
    await Linking.openURL(url);
    return true;
  } catch {
    Alert.alert('Error', 'Could not open link');
    return false;
  }
};

const FALLBACK_ITEMS = [
  { id: 'f1', text: '🚀 UK engineering jobs up 12.5% this quarter', type: 'jobs',    link: 'https://www.ons.gov.uk/employmentandlabourmarket' },
  { id: 'f2', text: '💰 Software engineer salaries reach £85,000 in London',         type: 'salary',    link: 'https://www.glassdoor.co.uk/Salaries' },
  { id: 'f3', text: '🌱 Government announces £3.5bn green engineering investment',   type: 'news',    link: 'https://www.gov.uk/government/news' },
  { id: 'f4', text: '📈 AI engineer demand surges across UK tech hubs',              type: 'trend',    link: 'https://www.reed.co.uk/career-advice' },
  { id: 'f5', text: '🎓 Engineering apprenticeship registrations hit record high',   type: 'education', link: 'https://www.engc.org.uk' },
  { id: 'f6', text: '⚡ BREAKING: New UK Infrastructure Bill passed in Parliament',  type: 'breaking', link: 'https://www.gov.uk' },
  { id: 'f7', text: '📊 ONS: Engineering employment rate at 97.2% — highest ever',  type: 'UK',       link: 'https://www.ons.gov.uk' },
  { id: 'f8', text: '🏗️ HS2 Phase 2 engineering contracts now open for tender',      type: 'policy',   link: 'https://www.hs2.org.uk' },
];

/**
 * Props:
 * - items: Array<{ id, title?, text?, url?, link?, source?, category?, type? }>
 * - onPress: (item) => void
 * - intervalMs: number (default 5000)
 * - showLiveBadge: boolean (default true)
 * - compact: boolean (default false)
 */
export default function NewsTicker({
  items = [],
  onPress,
  intervalMs = 5000,
  showLiveBadge = true,
  compact = false,
}) {
  const updates = useMemo(() => {
    const live = Array.isArray(items) ? items : [];
    const mapped = live.filter(Boolean).slice(0, 15).map((it, idx) => {
      const title  = it.text || it.title || 'UK update';
      const source = it.source ? ` • ${it.source}` : '';
      return {
        id: String(it.id ?? `live_${idx}`),
        text: `${title}${source}`,
        type: it.type || it.category || 'news',
        link: it.url || it.link || it.redirectUrl,
        raw:  it,
      };
    });
    return mapped.length ? mapped : FALLBACK_ITEMS;
  }, [items]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused,     setIsPaused]     = useState(false);
  const [isLive,       setIsLive]       = useState(true);

  const fadeAnim   = useRef(new Animated.Value(1)).current;
  const slideAnim  = useRef(new Animated.Value(0)).current;
  const pulseAnim  = useRef(new Animated.Value(1)).current;

  // Live dot pulse
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1.4, duration: 700, useNativeDriver: true }),
        Animated.timing(pulseAnim, { toValue: 1,   duration: 700, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, []);

  // Swipe gesture
  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) => Math.abs(g.dx) > 15,
      onPanResponderRelease: (_, g) => {
        if (g.dx < -30) goToNext();
        else if (g.dx > 30) goToPrev();
      },
    })
  ).current;

  useEffect(() => { setCurrentIndex(0); }, [updates.length]);

  const transition = useCallback((direction, callback) => {
    const outX = direction === 'next' ? -60 : 60;
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 0, duration: 220, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: outX, duration: 220, useNativeDriver: true }),
    ]).start(() => {
      callback();
      slideAnim.setValue(-outX);
      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 220, useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 220, useNativeDriver: true }),
      ]).start();
    });
  }, []);

  const goToNext = useCallback(() => {
    transition('next', () => setCurrentIndex(p => (p + 1) % updates.length));
  }, [updates.length, transition]);

  const goToPrev = useCallback(() => {
    transition('prev', () => setCurrentIndex(p => (p === 0 ? updates.length - 1 : p - 1)));
  }, [updates.length, transition]);

  useEffect(() => {
    if (isPaused || updates.length === 0) return;
    const id = setInterval(goToNext, intervalMs);
    return () => clearInterval(id);
  }, [isPaused, updates.length, goToNext, intervalMs]);

  const current = updates[currentIndex];
  const cfg = CATEGORY_CONFIG[current?.type] || CATEGORY_CONFIG.default;
  const isBreaking = current?.type === 'breaking';

  const handlePress = async () => {
    const item = current?.raw ?? current;
    const opened = current?.link ? await safeOpenUrl(current.link) : false;
    if (!opened && typeof onPress === 'function') onPress(item);
  };

  if (!updates.length) return null;

  return (
    <View style={[styles.container, isBreaking && styles.containerBreaking]}>
      {/* Left: LIVE / BREAKING badge + category icon */}
      <View style={styles.leftSection}>
        {showLiveBadge && (
          <View style={[styles.liveBadge, isBreaking && styles.breakingBadge]}>
            {isBreaking ? (
              <MaterialCommunityIcons name="flash" size={12} color="#fff" />
            ) : (
              <Animated.View style={[styles.liveDot, { transform: [{ scale: pulseAnim }] }]} />
            )}
            <Text style={styles.liveBadgeText}>{isBreaking ? 'BREAKING' : 'LIVE'}</Text>
          </View>
        )}
        <View style={[styles.categoryIconWrap, { backgroundColor: cfg.bg }]}>
          <MaterialCommunityIcons name={cfg.icon} size={14} color={cfg.color} />
        </View>
      </View>

      {/* Centre: ticker text */}
      <TouchableOpacity
        style={styles.textArea}
        onPress={handlePress}
        activeOpacity={0.75}
        {...panResponder.panHandlers}
      >
        <Animated.View style={[styles.textRow, { opacity: fadeAnim, transform: [{ translateX: slideAnim }] }]}>
          <Text style={[styles.tickerText, isBreaking && styles.tickerTextBreaking]} numberOfLines={1}>
            {current?.text}
          </Text>
          {current?.link && (
            <MaterialCommunityIcons name="open-in-new" size={12} color="rgba(255,255,255,0.5)" style={{ marginLeft: 4 }} />
          )}
        </Animated.View>
      </TouchableOpacity>

      {/* Right: controls */}
      <View style={styles.controls}>
        <TouchableOpacity style={styles.ctrlBtn} onPress={goToPrev}>
          <MaterialCommunityIcons name="chevron-left" size={16} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={styles.ctrlBtn} onPress={() => setIsPaused(v => !v)}>
          <MaterialCommunityIcons name={isPaused ? 'play' : 'pause'} size={13} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={styles.ctrlBtn} onPress={goToNext}>
          <MaterialCommunityIcons name="chevron-right" size={16} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Bottom: dot indicators */}
      {!compact && (
        <View style={styles.dotRow}>
          {updates.slice(0, Math.min(updates.length, 10)).map((_, i) => (
            <TouchableOpacity key={i} onPress={() => transition('next', () => setCurrentIndex(i))}>
              <View style={[styles.dot, i === currentIndex && styles.dotActive]} />
            </TouchableOpacity>
          ))}
          {updates.length > 10 && (
            <Text style={styles.moreText}>+{updates.length - 10}</Text>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container:          { backgroundColor: '#0e4e96', paddingTop: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: '#0a3a73' },
  containerBreaking:  { backgroundColor: '#7f1d1d' },
  leftSection:        { flexDirection: 'row', alignItems: 'center', position: 'absolute', left: 12, top: 10, zIndex: 2 },
  liveBadge:          { flexDirection: 'row', alignItems: 'center', backgroundColor: '#dc2626', paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, marginRight: 6 },
  breakingBadge:      { backgroundColor: '#ff4444' },
  liveDot:            { width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff', marginRight: 4 },
  liveBadgeText:      { color: '#fff', fontSize: 9, fontWeight: '900', letterSpacing: 0.5 },
  categoryIconWrap:   { width: 22, height: 22, borderRadius: 6, justifyContent: 'center', alignItems: 'center' },
  textArea:           { marginLeft: 96, marginRight: 100, minHeight: 20, justifyContent: 'center', paddingVertical: 2 },
  textRow:            { flexDirection: 'row', alignItems: 'center' },
  tickerText:         { color: '#fff', fontSize: 13, fontWeight: '500', flex: 1 },
  tickerTextBreaking: { fontWeight: '700', color: '#fff' },
  controls:           { position: 'absolute', right: 10, top: 6, flexDirection: 'row', gap: 4 },
  ctrlBtn:            { width: 26, height: 26, borderRadius: 13, backgroundColor: 'rgba(255,255,255,0.18)', justifyContent: 'center', alignItems: 'center' },
  dotRow:             { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', paddingVertical: 6, gap: 4 },
  dot:                { width: 5, height: 5, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.3)' },
  dotActive:          { backgroundColor: '#fff', width: 16 },
  moreText:           { color: 'rgba(255,255,255,0.5)', fontSize: 10, marginLeft: 2 },
});
